import { useState } from "react";
import { useForm, FieldErrors } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { useFormExitGuard } from "@/hooks/use-form-exit-guard";
import { useSavingProgress } from "@/components/campos-formulario/form-shell/useSavingProgress";
import { comunicadoSistemaService } from "@/services/api/comunicado-sistema.service";
import { comunicadoSistemaKeys } from "@/services/api/queries/comunicado-sistema.queries";
import {
  ComunicadoSistemaFormData,
  comunicadoSistemaSchema,
} from "@/validations/comunicado-sistema-validation-schema";
import { toComunicadoPayload } from "./comunicado-payload";

const COMUNICADOS_LIST = "/admin/comunicados";

/** Orquestra a criação de um comunicado (nasce rascunho). Espelha os hooks de create. */
export function useComunicadoSistemaCreateForm() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const saving = useSavingProgress();

  const methods = useForm<ComunicadoSistemaFormData>({
    resolver: yupResolver(comunicadoSistemaSchema),
    mode: "onTouched",
    reValidateMode: "onChange",
    defaultValues: {
      titulo: "",
      resumo: "",
      conteudo: "",
      nivel: "INFORMATIVO",
      inicioEm: new Date(),
      fimEm: null,
      cargos: [],
      areas: [],
    },
  });

  const { isSubmitting, isValid, isDirty } = methods.formState;
  const isSubmitDisabled = !isValid || isSubmitting;

  // Publicar é um PATCH separado (o comunicado nasce rascunho, §2.1). "Salvar e
  // publicar" é só o atalho de encadear os dois: sem endpoint novo, e o rascunho
  // continua sendo o caminho padrão para quem quer revisar antes.
  const [publicou, setPublicou] = useState(false);

  const salvar = (data: ComunicadoSistemaFormData, publicar: boolean) => {
    if (isSubmitting) return;
    setPublicou(publicar);
    void saving.run(
      async () => {
        const criado = await comunicadoSistemaService.create(toComunicadoPayload(data));
        if (publicar) await comunicadoSistemaService.publicar(criado.id);
        await qc.invalidateQueries({ queryKey: comunicadoSistemaKeys.all });
        return true;
      },
      () => navigate(COMUNICADOS_LIST),
    );
  };

  const onSubmit = (data: ComunicadoSistemaFormData) => salvar(data, false);

  const onError = (errors: FieldErrors<ComunicadoSistemaFormData>) => {
    console.error("Validation errors:", errors);
  };

  const exitGuard = useFormExitGuard({
    isDirty,
    isSubmitting,
    onExit: () => navigate(COMUNICADOS_LIST),
  });

  return {
    methods,
    onSubmit,
    onError,
    // Passa pelo mesmo `handleSubmit` do rascunho: publicar não pula validação.
    onPublicar: methods.handleSubmit((data) => salvar(data, true), onError),
    handleCancel: exitGuard.requestExit,
    exitGuard,
    isSubmitting,
    isSubmitDisabled,
    submitLabel: "Criar rascunho",
    publicou,
    // Diálogo de progresso
    savingOpen: saving.isOpen,
    savingStatus: saving.dialogStatus,
    handleSavingComplete: saving.handleComplete,
    handleSavingErrorClose: saving.reset,
  };
}

export type UseComunicadoSistemaCreateFormReturn = ReturnType<
  typeof useComunicadoSistemaCreateForm
>;
